import Dialog, { DialogProps } from '../Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Button } from 'common/ui/Button';
import Text from 'common/ui/Text';
import { Stack } from '../Layout';
import CircularProgress from '@mui/material/CircularProgress';
import { Colors } from 'styles';
import React from 'react';

type DialogConfirmProps = {
  slug: string;
  children: React.ReactNode;
  footerStart?: React.ReactNode;
  variant?: 'dark' | 'light';
  dialogWidth?: string;
  contentWidth?: string;
  confirmButtonCss?: string;
  cancelButtonCss?: string;
  confirmButtonVariant?: 'primary' | 'secondary' | 'light' | 'error' | 'ghost';
  cancelButtonVariant?: 'primary' | 'secondary' | 'light' | 'ghost';
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => undefined | Promise<void> | void;
  onCancel?: () => void;
  isConfirmLoading?: boolean;
} & DialogProps;

const DialogConfirm = ({
  slug,
  children,
  footerStart,
  variant = 'dark',
  dialogWidth = '360px',
  contentWidth = '295px',
  confirmButtonCss,
  cancelButtonCss,
  confirmButtonVariant = 'secondary',
  cancelButtonVariant = 'ghost',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isConfirmLoading = false,
  ...props
}: DialogConfirmProps) => {
  return (
    <Dialog
      {...props}
      variant={variant}
      slotProps={{
        paper: {
          sx: {
            padding: '20px 25px 25px 25px',
            width: dialogWidth,
          },
        },
      }}
    >
      <DialogContent
        sx={{
          width: contentWidth,
          textAlign: 'center',
        }}
        data-testid={`${slug}-modal-testid`}
      >
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: footerStart == null ? 'center' : 'space-between',
          alignItems: 'center',
          padding: 0,
          width: '100%',
        }}
      >
        {footerStart != null ? (
          <div
            css={`
              margin-right: 8px;
            `}
          >
            {footerStart}
          </div>
        ) : null}
        <div
          css={`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          {onCancel !== undefined ? (
            <Button
              id={`${slug}-cancel-button`}
              variant={cancelButtonVariant}
              onClick={onCancel}
              css={cancelButtonCss}
            >
              <Text
                css={`
                  white-space: nowrap;
                `}
                color={cancelButtonVariant === 'light' ? 'black' : 'white'}
              >
                {cancelText}
              </Text>
            </Button>
          ) : null}
          {onConfirm !== undefined ? (
            <Button
              type="submit"
              id={`${slug}-submit-button`}
              variant={confirmButtonVariant}
              onClick={onConfirm}
              disabled={isConfirmLoading}
              css={confirmButtonCss}
            >
              {isConfirmLoading ? (
                <Stack space="small">
                  <div
                    css={`
                      margin: 0 12px -5px -15px;
                      padding: 2px 0 0 0;
                    `}
                  >
                    <CircularProgress size="20px" sx={{ color: Colors.white }} />
                  </div>
                  <Text
                    css={`
                      white-space: nowrap;
                    `}
                    color={Colors.gray7}
                  >
                    {confirmText}
                  </Text>
                </Stack>
              ) : (
                <Text
                  css={`
                    white-space: nowrap;
                  `}
                  color={
                    confirmButtonVariant === 'secondary'
                      ? 'secondary'
                      : confirmButtonVariant === 'light'
                        ? 'black'
                        : 'white'
                  }
                >
                  {confirmText}
                </Text>
              )}
            </Button>
          ) : null}
        </div>
      </DialogActions>
    </Dialog>
  );
};
export default DialogConfirm;
